'use client'

import {useEffect, useState} from 'react'
import {ArrowLeft, ChevronRight, Headphones, RotateCw} from 'lucide-react'

type GlobalErrorPageProps = {
    fallbackHref: string
    heroSrc: string
    localeResolver?: () => keyof typeof copy
    logoSrc: string
    retry?: () => void
}

const copy = {
    pl: {
        eyebrow: 'Problemy techniczne',
        title: 'Mamy chwilowe problemy techniczne',
        description: <>Pracujemy nad ich usunięciem.<br/>Spróbuj ponownie za chwilę albo wróć do poprzedniej strony.</>,
        retry: 'Załaduj ponownie',
        back: 'Wróć',
        support: 'Problem nadal występuje?',
        help: 'Zgłoś go do Fitro',
        documentTitle: 'Problemy techniczne | Fitro',
    },
    en: {
        eyebrow: 'Technical issues',
        title: "We're having technical issues",
        description: <>We&apos;re working on a fix.<br/>Try again in a moment or go back to the previous page.</>,
        retry: 'Reload',
        back: 'Go back',
        support: 'Still having trouble?',
        help: 'Contact Fitro',
        documentTitle: 'Technical issues | Fitro',
    },
}

const styles = `
*{box-sizing:border-box}
html,body{margin:0;min-height:100%;background:#fff}
button,a{font:inherit}
.fitro-error-page{min-height:100dvh;background:#fff;color:#111827;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.fitro-error-shell{position:relative;width:100%;max-width:430px;min-height:100dvh;margin:0 auto;overflow:hidden;background:#fff}
.fitro-error-hero{position:absolute;top:30px;left:0;width:100%;height:auto;pointer-events:none;user-select:none}
.fitro-error-logo{position:absolute;z-index:1;top:12px;left:24px;width:88px;height:auto}
.fitro-error-content{position:relative;z-index:2;padding:clamp(414px,calc(130vw - 93px),466px) clamp(29px,calc(7.5vw - .25px),32px) 24px}
.fitro-error-eyebrow{margin:0 0 18px;color:#8592bd;font-size:12px;font-weight:700;letter-spacing:.2em;text-transform:uppercase}
.fitro-error-title{margin:0;color:#101828;font-size:32px;font-weight:700;line-height:1.16;letter-spacing:-.035em}
.fitro-error-copy{max-width:342px;margin:clamp(13px,calc(10vw - 26px),17px) 0 0;color:#27324a;font-size:15.5px;line-height:1.68}
.fitro-error-actions{display:grid;gap:clamp(12px,calc(2.5vw + 2.25px),13px);margin-top:clamp(29px,calc(20vw - 49px),37px)}
.fitro-error-action{display:flex;height:clamp(59px,calc(15vw + .5px),65px);align-items:center;justify-content:center;gap:14px;border-radius:9px;font-size:clamp(16px,calc(2.5vw + 6.25px),17px);font-weight:700;text-decoration:none;transition:transform .15s ease,box-shadow .15s ease}
@media (hover:hover) and (pointer:fine){.fitro-error-action:hover{transform:translateY(-1px)}}
.fitro-error-action:focus-visible,.fitro-error-help:focus-visible{outline:3px solid rgba(79,70,229,.3);outline-offset:3px}
.fitro-error-primary{border:0;background:linear-gradient(110deg,#4459ee 0%,#7345c5 100%);box-shadow:0 12px 24px rgba(79,70,229,.2);color:#fff;cursor:pointer}
.fitro-error-secondary{border:1.5px solid #5367f5;background:#fff;color:#5367f5}
.fitro-error-support{display:flex;align-items:center;gap:10px;margin-top:24px;padding-top:18px;border-top:1px solid #dce1ea;color:#27324a;font-size:12.5px;line-height:1.35}
.fitro-error-support-icon{display:grid;width:38px;height:38px;flex:0 0 auto;place-items:center;border-radius:999px;background:#f0f2ff;color:#253d98}
.fitro-error-help{display:inline-flex;align-items:center;gap:3px;color:#5266ee;font-weight:500;text-decoration:none;white-space:nowrap}
@media (min-width:700px){
  .fitro-error-shell{max-width:none}
  .fitro-error-logo{top:28px;left:clamp(40px,7vw,72px);width:98px}
  .fitro-error-hero{top:0;width:100%;height:44vh;object-fit:cover;object-position:center 55%}
  .fitro-error-content{max-width:720px;padding:46vh clamp(48px,8vw,88px) 48px}
  .fitro-error-title{font-size:40px}
  .fitro-error-copy{max-width:520px;font-size:17px}
  .fitro-error-actions{grid-template-columns:repeat(2,minmax(0,1fr));max-width:620px}
  .fitro-error-support{max-width:620px}
}
@media (min-width:900px){
  .fitro-error-logo{top:40px;left:6vw;width:105px}
  .fitro-error-hero{left:auto;right:0;width:48vw;height:100dvh;object-position:center}
  .fitro-error-content{max-width:min(42vw,520px);margin-left:6vw;padding:clamp(160px,20vh,200px) 0 48px}
  .fitro-error-eyebrow{margin-bottom:22px;font-size:13px}
  .fitro-error-title{font-size:40px}
  .fitro-error-copy{margin-top:20px;font-size:18px;line-height:1.7}
  .fitro-error-actions{grid-template-columns:1fr;max-width:430px;margin-top:36px}
  .fitro-error-action{height:64px}
  .fitro-error-support{max-width:430px;margin-top:28px}
}
@media (min-width:1280px){
  .fitro-error-logo{top:48px;left:clamp(64px,7vw,112px);width:112px}
  .fitro-error-hero{width:55vw}
  .fitro-error-content{max-width:600px;margin-left:clamp(64px,7vw,112px);padding:clamp(190px,23vh,260px) 0 64px}
  .fitro-error-title{font-size:48px}
  .fitro-error-actions{margin-top:42px}
}
@media (max-height:780px) and (max-width:699px){
  .fitro-error-content{padding-top:380px}
  .fitro-error-actions{margin-top:26px}
  .fitro-error-support{margin-top:22px}
}
@media (prefers-reduced-motion:reduce){.fitro-error-action{transition:none}.fitro-error-action:hover{transform:none}}
`

export function GlobalErrorPage({fallbackHref, heroSrc, localeResolver, logoSrc, retry}: GlobalErrorPageProps) {
    const [locale, setLocale] = useState<keyof typeof copy>('pl')

    useEffect(() => {
        let nextLocale: keyof typeof copy = 'pl'
        if (localeResolver) {
            try {
                nextLocale = localeResolver()
            } catch {
                // The recovery page must still render when locale detection fails.
            }
            document.documentElement.lang = nextLocale
            setLocale(nextLocale)
            return
        }
        let storedLocale: string | null = null
        try {
            storedLocale = window.localStorage.getItem('fitro-locale')
        } catch {
            // The recovery page must still render when storage is unavailable.
        }
        if (storedLocale === 'pl' || storedLocale === 'en') {
            nextLocale = storedLocale
        } else {
            const browserLocales = navigator.languages?.length ? navigator.languages : [navigator.language]
            for (const browserLocale of browserLocales) {
                const language = browserLocale.toLowerCase().split('-')[0]
                if (language === 'pl' || language === 'en') {
                    nextLocale = language
                    break
                }
            }
        }
        document.documentElement.lang = nextLocale
        setLocale(nextLocale)
    }, [localeResolver])

    const goBack = () => {
        if (window.history.length > 1) {
            window.history.back()
            return
        }
        window.location.assign(fallbackHref)
    }

    const text = copy[locale]

    useEffect(() => {
        // Next.js streams the layout's metadata title after mount, so keep re-applying ours while shown.
        const applyTitle = () => {
            if (document.title !== text.documentTitle) document.title = text.documentTitle
        }
        applyTitle()
        const observer = new MutationObserver(applyTitle)
        observer.observe(document.head, {childList: true, subtree: true, characterData: true})
        return () => observer.disconnect()
    }, [text.documentTitle])

    return (
        <main className="fitro-error-page">
            <style>{styles}</style>
            <section className="fitro-error-shell" aria-labelledby="fitro-error-title">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className="fitro-error-hero" src={heroSrc} alt=""/>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className="fitro-error-logo" src={logoSrc} alt="Fitro"/>
                <div className="fitro-error-content">
                    <p className="fitro-error-eyebrow">{text.eyebrow}</p>
                    <h1 className="fitro-error-title" id="fitro-error-title">{text.title}</h1>
                    <p className="fitro-error-copy">{text.description}</p>

                    <div className="fitro-error-actions">
                        <button className="fitro-error-action fitro-error-primary" type="button" onClick={retry ?? (() => window.location.reload())}>
                            <RotateCw size={23} aria-hidden="true"/>
                            {text.retry}
                        </button>
                        <button className="fitro-error-action fitro-error-secondary" type="button" onClick={goBack}>
                            <ArrowLeft size={25} aria-hidden="true"/>
                            {text.back}
                        </button>
                    </div>

                    <div className="fitro-error-support">
                        <span className="fitro-error-support-icon" aria-hidden="true"><Headphones size={21}/></span>
                        <span>
                            {text.support}{' '}
                            <a className="fitro-error-help" href="mailto:kontakt@fitro.pl">
                                {text.help} <ChevronRight size={18} aria-hidden="true"/>
                            </a>
                        </span>
                    </div>
                </div>
            </section>
        </main>
    )
}
